import {
  buildActiveFilterDescriptors,
  resolveActiveFilterColumnVisibilityToggle,
} from "@/components/data-table/utils/activeFilterDescriptors";
import { buildActiveFiltersBarCountLabel } from "@/components/data-table/utils/buildActiveFiltersBarCountLabel";
import { useDataTableSettings } from "@/components/data-table/hooks/useDataTableSettings";
import useTableColumnVisibility from "@/components/data-table/hooks/useTableColumnVisibility";
import { useIsMobile } from "@/hooks/use-mobile";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ACTIVE_FILTERS_BAR_SEGMENT_CLASS,
  activeFiltersBarToneClasses,
  resolveActiveFiltersStripTone,
} from "./activeFiltersBarShared";
import { ActiveFiltersBarDescriptorSlot } from "./ActiveFiltersBarDescriptorSlot";
import { ActiveFiltersBarDivider } from "./ActiveFiltersBarDivider";
import { ActiveFiltersBarMobileUnifiedStrip } from "./ActiveFiltersBarMobileUnifiedStrip";
import { ActiveFiltersBarSortSlot } from "./ActiveFiltersBarSortSlot";
import { ActiveFiltersBarTrack } from "./ActiveFiltersBarTrack";

const DataTableActiveFiltersBarComp = () => {
  const { t, i18n } = useTranslation();
  const { features, table, options } = useDataTableSettings();
  const { columnVisibility } = useTableColumnVisibility();
  const { enableFilters, enableSorting, enableActiveFiltersBar } = features;

  const showFilterColumn = enableFilters !== false;
  const showSortColumn = enableSorting !== false;

  const descriptors = useMemo(
    () =>
      buildActiveFilterDescriptors(table, options.rawFilters, columnVisibility),
    [table, options.rawFilters, columnVisibility],
  );

  const hiddenFilterCount = useMemo(
    () => descriptors.filter((d) => d.hidden).length,
    [descriptors],
  );

  const filtersBarCombinedLabel = useMemo(
    () =>
      buildActiveFiltersBarCountLabel({
        t,
        i18n,
        totalCommittedFilterCount: descriptors.length,
        hiddenCommittedFilterCount: hiddenFilterCount,
      }),
    [descriptors.length, hiddenFilterCount, t, i18n],
  );

  const hasSorting = options.isSorted();

  const sortPrimary = options.sorting[0];
  const sortDirection = useMemo<"asc" | "desc" | null>(() => {
    if (!sortPrimary) return null;
    return sortPrimary.desc ? "desc" : "asc";
  }, [sortPrimary]);

  const sortDetailLine = useMemo(() => {
    if (!sortPrimary) return null;
    const col = table.getColumn(sortPrimary.id);
    const headerTranslationKey =
      typeof col?.columnDef.header === "string"
        ? col.columnDef.header
        : undefined;
    const column = headerTranslationKey
      ? t(headerTranslationKey)
      : sortPrimary.id;
    const direction = sortPrimary.desc
      ? t("table.sortDescending")
      : t("table.sortAscending");
    return t("table.activeFilters.sortingApplied", {
      column,
      direction,
    });
  }, [sortPrimary, table, t]);

  const stripTone = resolveActiveFiltersStripTone({
    hasHiddenCommittedFilter: hiddenFilterCount > 0,
  });
  const {
    barToneClass,
    dividerClass,
    idleLabelClass,
    triggerInteractionsClass,
    activeGlyphClass,
  } = activeFiltersBarToneClasses(stripTone);

  const clearAllFilters = useCallback(() => {
    for (const d of descriptors) {
      options.resetFilterById(d.columnId);
    }
  }, [options, descriptors]);

  const getColumnVisibilityToggle = useCallback(
    (columnId: string) =>
      resolveActiveFilterColumnVisibilityToggle(table, columnId),
    [table],
  );

  const isMobileViewport = useIsMobile();

  if (!enableActiveFiltersBar || (!showFilterColumn && !showSortColumn)) {
    return null;
  }

  if (isMobileViewport && showFilterColumn && showSortColumn) {
    return (
      <ActiveFiltersBarTrack barToneClass={barToneClass}>
        <ActiveFiltersBarMobileUnifiedStrip
          idleLabelClass={idleLabelClass}
          triggerInteractionsClass={triggerInteractionsClass}
          activeGlyphClass={activeGlyphClass}
          dividerClass={dividerClass}
          countLabel={filtersBarCombinedLabel}
          noneFiltersLabel={t("table.activeFilters.noneActiveFilters")}
          descriptors={descriptors}
          onRemoveColumnFilter={options.resetFilterById}
          onClearAllFilters={clearAllFilters}
          getColumnVisibilityToggle={getColumnVisibilityToggle}
          sortSummaryLine={hasSorting ? sortDetailLine : null}
          sortDirection={hasSorting ? sortDirection : null}
          noneActiveSortingLabel={t("table.activeFilters.noneActiveSorting")}
          onClearSorting={options.resetSorting}
        />
      </ActiveFiltersBarTrack>
    );
  }

  return (
    <ActiveFiltersBarTrack barToneClass={barToneClass}>
      {showFilterColumn ? (
        <ActiveFiltersBarDescriptorSlot
          segmentClass={ACTIVE_FILTERS_BAR_SEGMENT_CLASS}
          idleLabelClass={idleLabelClass}
          triggerInteractionsClass={triggerInteractionsClass}
          activeGlyphClass={activeGlyphClass}
          countLabel={filtersBarCombinedLabel}
          noneLabel={t("table.activeFilters.noneActiveFilters")}
          descriptors={descriptors}
          onRemoveColumnFilter={options.resetFilterById}
          onClearAll={clearAllFilters}
          getColumnVisibilityToggle={getColumnVisibilityToggle}
        />
      ) : null}
      {showFilterColumn && showSortColumn ? (
        <ActiveFiltersBarDivider dividerClass={dividerClass} />
      ) : null}
      {showSortColumn ? (
        <ActiveFiltersBarSortSlot
          segmentClass={ACTIVE_FILTERS_BAR_SEGMENT_CLASS}
          idleLabelClass={idleLabelClass}
          triggerInteractionsClass={triggerInteractionsClass}
          activeGlyphClass={activeGlyphClass}
          sortSummaryLine={hasSorting ? sortDetailLine : null}
          sortingActiveBriefLabel={t("table.activeFilters.sortingActiveBrief")}
          sortDirection={hasSorting ? sortDirection : null}
          noneActiveSortingLabel={t("table.activeFilters.noneActiveSorting")}
          onClearSorting={options.resetSorting}
        />
      ) : null}
    </ActiveFiltersBarTrack>
  );
};

export const DataTableActiveFiltersBar = memo(DataTableActiveFiltersBarComp);
